// DEPRECATED: This component has been replaced by UnifiedScrollArea for better touch handling
// Please use UnifiedScrollArea instead for new implementations
import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cn } from "@/lib/utils";

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  enableHorizontalScroll?: boolean;
  viewportProps?: React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Viewport>;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(({ className, children, enableHorizontalScroll = false, viewportProps, ...props }, ref) => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const lastMouseXRef = React.useRef(0);
  const isCardDraggingRef = React.useRef(false);
  const autoScrollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (!enableHorizontalScroll || !viewportRef.current) return;

    const viewport = viewportRef.current;
    
    // Edge-based auto-scroll during drag
    const handleAutoScroll = (clientX: number) => {
      if (!viewport || !isCardDraggingRef.current) return;
      
      const viewportRect = viewport.getBoundingClientRect();
      const edgeThreshold = 100;
      const scrollSpeed = 5;
      
      // Clear existing auto-scroll
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      
      // Check if near left edge
      if (clientX - viewportRect.left < edgeThreshold && viewport.scrollLeft > 0) {
        autoScrollIntervalRef.current = setInterval(() => {
          if (viewport.scrollLeft > 0) {
            viewport.scrollLeft = Math.max(0, viewport.scrollLeft - scrollSpeed);
          } else {
            clearInterval(autoScrollIntervalRef.current!);
            autoScrollIntervalRef.current = null;
          }
        }, 16);
      }
      // Check if near right edge
      else if (viewportRect.right - clientX < edgeThreshold) {
        const maxScroll = viewport.scrollWidth - viewport.clientWidth;
        if (viewport.scrollLeft < maxScroll) {
          autoScrollIntervalRef.current = setInterval(() => {
            if (viewport.scrollLeft < maxScroll) {
              viewport.scrollLeft = Math.min(maxScroll, viewport.scrollLeft + scrollSpeed);
            } else {
              clearInterval(autoScrollIntervalRef.current!);
              autoScrollIntervalRef.current = null;
            }
          }, 16);
        }
      }
    };

    const stopAutoScroll = () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (!viewport) return;

      // Check if card is being dragged
      const isDragging = document.querySelector('[data-rbd-drag-handle-dragging-id]') !== null;
      
      const targetElement = e.target as Element;
      // Allow vertical scrolling in column areas when not dragging
      const isInsideColumn = targetElement.closest(".kanban-column") || 
                            targetElement.closest('[class*="overflow-y-auto"]') ||
                            targetElement.closest('[data-radix-scroll-area-viewport]');

      if (isInsideColumn && !isDragging) {
        // Allow normal vertical scrolling inside columns
        return;
      } else if (!isDragging && enableHorizontalScroll) {
        // Enable horizontal board panning when not inside a column
        e.preventDefault();
        viewport.scrollLeft += e.deltaY;
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      
      // Check for active card dragging
      const isRbdActive = document.querySelector('[data-rbd-drag-handle-dragging-id]') !== null;
      
      // Don't interfere with card dragging
      if (isRbdActive) {
        isCardDraggingRef.current = true;
        return;
      }

      // Don't interfere with interactive elements or column content
      const isInteractiveElement = target.closest('button') ||
                                  target.closest('input') ||
                                  target.closest('textarea') ||
                                  target.closest('select') ||
                                  target.closest('[role="button"]') ||
                                  target.closest('a') ||
                                  target.closest('[data-rbd-draggable-id]') ||
                                  target.closest('.dropdown-menu') ||
                                  target.closest('.context-menu') ||
                                  target.closest('.kanban-column') ||
                                  target.closest('[data-radix-scroll-area-viewport]');

      if (isInteractiveElement) {
        return;
      }

      isDraggingRef.current = true;
      lastMouseXRef.current = e.clientX;
      viewport.style.cursor = 'grabbing';
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Handle auto-scroll during card drag
      if (isCardDraggingRef.current) {
        handleAutoScroll(e.clientX);
        return;
      }
      
      // Check for active RBD dragging
      const isRbdActive = document.querySelector('[data-rbd-drag-handle-dragging-id]') !== null;
      if (isRbdActive && !isDraggingRef.current) {
        isCardDraggingRef.current = true;
        return;
      }
      
      if (!isDraggingRef.current) return;
      
      const deltaX = e.clientX - lastMouseXRef.current;
      viewport.scrollLeft -= deltaX;
      lastMouseXRef.current = e.clientX;
      e.preventDefault();
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      isCardDraggingRef.current = false;
      stopAutoScroll();
      viewport.style.cursor = enableHorizontalScroll ? 'grab' : 'default';
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const handleMouseLeave = () => {
      if (!isCardDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
      stopAutoScroll();
    };

    // React Beautiful DnD event handlers
    const handleDragStart = () => {
      isCardDraggingRef.current = true;
      isDraggingRef.current = false;
      stopAutoScroll();
    };

    const handleDragEnd = () => {
      isCardDraggingRef.current = false;
      stopAutoScroll();
    };

    // Set initial cursor and touch behavior
    viewport.style.cursor = enableHorizontalScroll ? 'grab' : 'default';
    viewport.style.touchAction = 'pan-y'; // Always allow vertical scrolling

    // Add event listeners
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    if (enableHorizontalScroll) {
      viewport.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      viewport.addEventListener('mouseleave', handleMouseLeave);
    }
    
    // Listen for drag events
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);
    
    return () => {
      stopAutoScroll();
      viewport.removeEventListener('wheel', handleWheel);
      if (enableHorizontalScroll) {
        viewport.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        viewport.removeEventListener('mouseleave', handleMouseLeave);
      }
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, [enableHorizontalScroll]);

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative", enableHorizontalScroll ? "overflow-hidden" : "overflow-auto", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport 
        ref={viewportRef}
        className={cn(
          "h-full w-full rounded-[inherit]",
          enableHorizontalScroll ? "overflow-x-auto overflow-y-hidden" : "overflow-y-auto"
        )}
        {...viewportProps}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors z-50",
      orientation === "vertical" &&
        "h-[100%] w-2.5 border-l border-t-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px] absolute bottom-0 left-0 right-0",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border opacity-50 hover:opacity-100 transition-opacity" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
